import { Injectable } from "@angular/core";
import { Observable, lastValueFrom, map } from "rxjs";
import { Organisation } from "src/app/models/organisation";
import { IdentityOrganisationApiClient } from 'src/app/services/api-clients/identity-organisation.api-client';
import { LoggingService } from "src/app/services/logging.service";
import { User } from "src/app/models/user";
import { AuthenticationService } from "src/app/services/authentication.service";
import { IdentityBrandingService } from "src/app/services/identityBranding.service";
import { OrganisationSettingsApiClient } from "src/app/services/organisation-settings.apiclient";
import { OrganisationApiClient } from "src/app/services/organisation.apiclient";
import { ToastService } from "src/app/services/toast.service";
import { Papertypes } from "src/app/models/papertypes";
import { DomSanitizer } from "@angular/platform-browser";
import { BlobHelper } from "src/app/_helpers/blobHelper";
import { PlatformSettings } from "src/app/constants/settings.enum";
import { WhiteLabel } from "src/app/models/whiteLabel";
import { IdentityWhitelabelApiClient } from "src/app/services/api-clients/identity-whitelabel.api-client";
import { IdentitySystemApiClient } from "src/app/services/api-clients/identity-system.api-client";
import { System } from "src/app/models/system";
import { PlatformHubService } from "src/app/services/platformhub.service";

@Injectable({
  providedIn: 'root'
})
export class SettingsService {

  public currentUserId: string = "";

  user!: User;
  papertypes: Papertypes[] = [];
  twofactorrequired: any;
  identificationmethods: any;
  idin: any;
  ssoprovider: any;
  ssorequired: any;
  detachedCertificate: any;
  organisation: Organisation = new Organisation;
  whiteLabel: WhiteLabel = new WhiteLabel;
  system: System = new System;
  organisationGuid!: string;
  whiteLabelGuid!: string;
  systemGuid!: string;
  storageAvailable: boolean = false;
  brandingAvailable: boolean = false;
  _plugins!: Observable<any[]>;
  _orgSettings!: Observable<any[]>;
  _orgIdentitySettings!: Observable<any[]>;
  _organisation!: Observable<Organisation>;
  _selectedOrganisationplugins: [] | undefined;

  constructor(
    private authenticationService: AuthenticationService,
    private identityOrganisationService: IdentityOrganisationApiClient,
    private identityWhiteLabelService: IdentityWhitelabelApiClient,
    private identitySystemApiClient: IdentitySystemApiClient,
    private organisationApiClient: OrganisationApiClient,
    private identityBrandingService: IdentityBrandingService,
    private loggingService: LoggingService,
    private platformHubService: PlatformHubService,
    private toastService: ToastService,
    private organisationSettingsApiClient: OrganisationSettingsApiClient,
    private sanitizer: DomSanitizer,
    private blobHelper: BlobHelper) {

    this.authenticationService.currentUser.subscribe({
      next: (userData: User) => {
        if (this.organisationGuid != userData.organisationGuid) {
          this.organisationGuid = userData.organisationGuid;
          if (userData.isOrganisationAdmin || userData.isWhitelabelAdmin || userData.isSystemAdmin) {
            this.getOrganisation(userData.organisationGuid, userData);
          }
        }
        this.user = userData;
        this.currentUserId = this.user.sub;
        if (this.organisationGuid) this._orgSettings = this.organisationSettingsApiClient.getOrganisationSettings(this.organisationGuid);
        this._plugins = this.organisationApiClient.getOrganisationPlugins(this.organisationGuid);
        if (userData.isOrganisationAdmin || userData.isWhitelabelAdmin || userData.isSystemAdmin) {
          this._organisation = this.identityOrganisationService.getOrganisation(this.organisationGuid);
        }
      },
      error: (error: any) => {
        this.loggingService.logException(error);
      }
    });
  }

  getOrganisation(organisationGuid: string, userData: User) {
    this.identityOrganisationService.getOrganisation(organisationGuid).subscribe({
      next: (organisation) => {
        this.organisation = organisation;
        this.organisationGuid = this.organisation?.organisation?.guid;
        if (userData.isWhitelabelAdmin || userData.isSystemAdmin) {
          this.getwhitelabel(this.organisation.organisation.enterprise?.guid, userData);
        }
      }
    });
  }

  getwhitelabel(whiteLabelGuid: string, userData: User) {
    this.identityWhiteLabelService.getWhitelabel(whiteLabelGuid).subscribe({
      next: (whiteLabel) => {
        this.whiteLabel = whiteLabel;
        this.whiteLabelGuid = this.whiteLabel?.whiteLabel?.guid;
        if (userData.isSystemAdmin) {
          this.getSystem(this.whiteLabel.whiteLabel.whitelabelProvider?.guid);
        }
      }
    });
  }

  getSystem(systemGuid: string) {
    this.identitySystemApiClient.getSystem(systemGuid).subscribe({
      next: (system) => {
        this.system = system;
        this.systemGuid = this.system?.system?.guid;
      }
    });
  }

  getOrganisationSettings(settings: PlatformSettings[]) {
    return this._orgSettings.pipe(
      map((_settings: any[]) => _settings.filter((setting: any) => settings.includes(setting.key))));
  }

  getOrganisationPlugins() {
    return this._plugins;
  }

  public getStationery(selectedOrganisationGuid?: string): Papertypes[] {
    // Clear papertypes before loading
    this.papertypes = [];

    let getPapertypes = this.organisationApiClient.getPapertypes(selectedOrganisationGuid);
    lastValueFrom(getPapertypes).then((data: any) => {
      for (let i: number = 0; i < data.length; i++) {
        if (data[i].id !== "00000000-0000-0000-0000-000000000000" && !this.papertypes.some((s: any) => s.id === data[i].id)) {
          this.papertypes.push({
            id: data[i].id,
            name: data[i].name,
            thumbnail: data[i].thumbnail != null
              ? this.sanitizer.bypassSecurityTrustUrl(
                window.URL.createObjectURL(this.blobHelper.base64toBlob(data[i].thumbnail, "image/jpeg", 512)))
              : null,
            ordinal: data[i].ordinal
          });
        }
      }
      this.papertypes.sort((a, b) => a.ordinal - b.ordinal);
    })
      .catch((error) => this.loggingService.logException(error));
    return this.papertypes;
  }

  getOrganisationDetails() {
    return this.organisation;
  }

  getTwoFactorrequired(selectedOrganisationGuid?: string) {
    return this.identityOrganisationService.getTwoFactorRequired(selectedOrganisationGuid ? selectedOrganisationGuid : this.organisationGuid);
  }

  getIdentificationmethod(selectedOrganisationGuid?: string) {
    return this.identityOrganisationService.getIdentificationmethod(selectedOrganisationGuid ? selectedOrganisationGuid : this.organisationGuid);
  }

  getIdin(selectedOrganisationGuid?: string) {
    return this.organisationSettingsApiClient.getIdin(selectedOrganisationGuid ? selectedOrganisationGuid : this.organisationGuid);
  }

  getSsoprovider(selectedOrganisationGuid?: string) {
    return this.identityOrganisationService.getSsoprovider(selectedOrganisationGuid ? selectedOrganisationGuid : this.organisationGuid);
  }

  getSsorequired(selectedOrganisationGuid?: string) {
    return this.identityOrganisationService.getSsorequired(selectedOrganisationGuid ? selectedOrganisationGuid : this.organisationGuid)
  }

  getViduaAuthentication(selectedOrganisationGuid?: string) {
    return this.identityOrganisationService.getViduaAuthentication(selectedOrganisationGuid ? selectedOrganisationGuid : this.organisationGuid)
  }

  getViduaIdentification(selectedOrganisationGuid?: string) {
    return this.identityOrganisationService.getViduaIdentification(selectedOrganisationGuid ? selectedOrganisationGuid : this.organisationGuid)
  }

  getDetachedCertificate(selectedOrganisationGuid?: string) {
    return this.organisationSettingsApiClient.getDetachedCertificate(selectedOrganisationGuid ? selectedOrganisationGuid : this.organisationGuid)
  }

  getBrandingStatusOrganisation(organisationGuid: string) {
    return this.identityBrandingService.getBrandingStatusOrganisation(organisationGuid);
  }
  getBrandingStatusWhiteLabel(whiteLabelGuid: string) {
    return this.identityBrandingService.getBrandingStatusWhiteLabel(whiteLabelGuid);
  }
  getBrandingStatusSystem(systemGuid: string) {
    return this.identityBrandingService.getBrandingStatusSystem(systemGuid);
  }

  updateSettings(settings: any, selectedOrganisationGuid: string) {
    this.organisationSettingsApiClient.postOrganisationSettings(selectedOrganisationGuid, settings).subscribe({
      next: () => {
        this.toastService.showSaved();
      },
      error: () => {

      }
    });
  }

  updatePlugins(plugins: any, selectedOrganisationGuid: string) {
    this.organisationApiClient.setPlugins(plugins, selectedOrganisationGuid).subscribe({
      next: () => {
        this.toastService.showSaved();
      },
      error: () => {

      }
    });
  }

  async updateGeneralSettings(event: any, selectedOrganisationGuid: string) {
    switch (event.key) {
      case "identificationMethod":
        (await this.identityOrganisationService.postIdentificationmethod(selectedOrganisationGuid ? selectedOrganisationGuid : this.organisationGuid, { identificationMethod: event.body })).subscribe({
          next: (data: any) => {
            this.toastService.showSaved();
          },
        })
        break;
      case "ssoProvider":
        (await this.identityOrganisationService.postSsoprovider(selectedOrganisationGuid ? selectedOrganisationGuid : this.organisationGuid, { SsoProvider: event.body.name })).subscribe({
          next: (data: any) => {
            this.toastService.showSaved();
          },
        })
        break;
      case "ssoRequired":
        (await this.identityOrganisationService.postSsorequired(selectedOrganisationGuid ? selectedOrganisationGuid : this.organisationGuid, { SsoRequired: event.body })).subscribe({
          next: (data: any) => {
            this.toastService.showSaved();
          },
        })
        break;
      case "twoFactorRequired":
        (await this.identityOrganisationService.postTwoFactorRequired(selectedOrganisationGuid ? selectedOrganisationGuid : this.organisationGuid, { TwoFactorRequired: event.body })).subscribe({
          next: (data: any) => {
            this.toastService.showSaved();
          },
        })
        break;
      case "idin":
        (await this.organisationSettingsApiClient.postIdin(selectedOrganisationGuid ? selectedOrganisationGuid : this.organisationGuid, { UseIdin: event.body })).subscribe({
          next: (data: any) => {
            this.toastService.showSaved();
          },
        })
        break;
      case "viduaAuthentication":
        (await this.identityOrganisationService.postViduaAuthentication(selectedOrganisationGuid ? selectedOrganisationGuid : this.organisationGuid, event.body)).subscribe({
          next: (data: any) => {
            this.toastService.showSaved();
          },
        })
        break;
      case "ViduaIdentification":
        (await this.identityOrganisationService.postViduaIdentification(selectedOrganisationGuid ? selectedOrganisationGuid : this.organisationGuid, event.body)).subscribe({
          next: (data: any) => {
            this.toastService.showSaved();
          },
        })
        break;
      case "detachedCertificate":
        (await this.organisationSettingsApiClient.postDetachedCertificate(selectedOrganisationGuid ? selectedOrganisationGuid : this.organisationGuid, { IncludeDetachedCertificate: event.body })).subscribe({
          next: (data: any) => {
            this.toastService.showSaved();
          },
        })
        break;
      case "ShowAllDocsInOrg":
        await this.platformHubService.loadRequests()
        this.toastService.showSaved();
        break;
      case "EnableSpecialistRequestWorkflows":
        this._orgSettings.subscribe((settingData: any[]) => {
          let settingIndex = settingData.findIndex(s => s.key === event.key);
          if (settingIndex > -1) settingData[settingIndex].value = event.body;
          else settingData.push(event);
        });
        this.organisationSettingsApiClient.postEnableSpecialistRequestWorkflows(selectedOrganisationGuid, event.body).subscribe({
          next: (response: any) => {
            this.toastService.showSaved();
          },
        });
        break;
    }
  }

  async updateOrganisation(organisation: Organisation) {
    this.organisation = organisation;
    if (!this.organisation.organisation.whitelabelEnabled) {
      await this.updateBrandingStatus("disable", organisation.organisation, false);
    }
    let updateOrganisationCall = await this.identityOrganisationService.updateOrganisation(this.organisation.organisation.guid, this.organisation);
    await lastValueFrom(updateOrganisationCall);
    this.storageAvailable = this.organisation.organisation.customStorageEnabled;
    this.brandingAvailable = this.organisation.organisation.whitelabelEnabled;

    this.toastService.showSaved();
  }

  async updateBrandingStatus(event: string, forOrganisation: any, showToast: boolean = true) {
    if (event === "enable") {
      let enableBrandingCall = await this.identityBrandingService.enableBrandingOrganisation(forOrganisation.guid);
      await lastValueFrom(enableBrandingCall);
      if (showToast) this.toastService.showSaved();
    } else if (event === "disable") {
      let disableBrandingCall = await this.identityBrandingService.disableBrandingOrganisation(forOrganisation.guid);
      await lastValueFrom(disableBrandingCall)
      if (showToast) this.toastService.showSaved();
    }
  }
  async updateBrandingStatusWhiteLabel(enabled: boolean, forWhiteLabelGuid: string) {
    if (enabled) {
      let enableBrandingCall = await this.identityBrandingService.enableBrandingWhiteLabel(forWhiteLabelGuid);
      await lastValueFrom(enableBrandingCall);
    } else {
      let disableBrandingCall = await this.identityBrandingService.disableBrandingWhiteLabel(forWhiteLabelGuid);
      await lastValueFrom(disableBrandingCall)
    }
  }

  async updateOrganisationAzureBlobstorage(blobstorageConnectionstring: any, organisationGuid: string) {
    (await this.identityOrganisationService.postAzureblobstorage(organisationGuid, { StorageAccount: blobstorageConnectionstring })).subscribe({
      next: (data: any) => {
        this.toastService.showSaved();
      },
    });
  }

  async updateWhitelabelAzureBlobstorage(blobstorageConnectionstring: any, whiteLabelGuid: string) {
    (await this.identityWhiteLabelService.postAzureblobstorage(whiteLabelGuid, { StorageAccount: blobstorageConnectionstring })).subscribe({
      next: (data: any) => {
        this.toastService.showSaved();
      }
    });
  }

  async updateSystemAzureBlobstorage(blobstorageConnectionstring: any, systemGuid: string) {
    (await this.identitySystemApiClient.postAzureblobstorage(systemGuid, { StorageAccount: blobstorageConnectionstring })).subscribe({
      next: (data: any) => {
        this.toastService.showSaved();
      },
    });
  }
}
